import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Header from "../components/Header";
import ContactList from "../components/ContactList";
import styles from "./EmergencyContacts.module.css";
import Button from "../components/Button";
import Modal from "../components/Modal";
import InputItem from "../components/InputItem";
import Loader from "components/Loader";
import { formattedPhone } from "components/shared/formattedPhone";
import { useEmergencyContacts } from "./useEmergencyContacts";
import { useTranslation } from "react-i18next";

export function EmergencyContacts() {
  const {t} = useTranslation();
  const history = useHistory();

  const {
    data,
    isError,
    isLoading,
    refetch,
    createEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
  } = useEmergencyContacts();

  const [isAddingContact, setIsAddingContact] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contact, setContact] = useState({
    firstName: "",
    lastName: "",
    phone: "",
  });

  const [contacts, setContacts] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setContacts(data);
    }
  }, [data]);

  //Triggers the input onChange
  const handleChange = (field, value) => {
    if (field === "phone") {
      const rawPhone = value.replace(/\D/g, "").slice(0, 10);
      setContact((prevState) => ({
        ...prevState,
        phone: rawPhone,
      }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        phoneNumber: "",
      }));
    } else {
      setContact((prevState) => ({
        ...prevState,
        [field]: value,
      }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  //Validate inputs
  const validateInputs = ({ firstName, lastName, phone }) => {
    const errors = {};

    if (!firstName) {
      errors.firstName = t("Please enter a first name");
    }

    if (!lastName) {
      errors.lastName = t("Please enter a last name");
    }

    if (!phone) {
      errors.phoneNumber = t("Please enter a phone number");
    } else {
      const cleanedPhone = phone.replace(/\D/g, "");
      if (cleanedPhone.length !== 10) {
        errors.phoneNumber = t("Please enter a valid phone number");
      }
    }

    return errors;
  };

  //Add emergency contact
  async function handleAddContact() {
    const { firstName, lastName, phone } = contact;

    const validationErrors = validateInputs({
      firstName,
      lastName,
      phone,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const newContact = {
      firstName,
      lastName,
      phoneNumber: phone.replace(/\D/g, ""),
    };

    await createEmergencyContact.mutateAsync(newContact);

    resetForm();
    setShowAddModal(false);
  }

  //Edit emergency contact
  async function handleUpdateContact(contact) {
    const { firstName, lastName, phone } = contact;

    const validationErrors = validateInputs({
      firstName,
      lastName,
      phone,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const updatedContact = {
      contactId: contact.contactId,
      updateData: {
        firstName,
        lastName,
        phoneNumber: phone.replace(/\D/g, ""),
      },
    };

    await updateEmergencyContact.mutateAsync(updatedContact);

    resetForm();
    setShowEditModal(false);
    history.push("/emergency-contacts");
  }

  //Delete emergency contact
  async function handleDeleteContact(contact) {
    await deleteEmergencyContact.mutateAsync(contact.contactId);

    resetForm();
    setShowDeleteConfirmation(false);
  }

  //Triggers edit button
  const handleEdit = (contact) => {
    setIsAddingContact(false);
    setShowEditModal(true);
    setContact({
      contactId: contact.contactId || "",
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      phone: formattedPhone(contact.phone || ""),
    });
  };

  //Handles to delete the contact
  const handleDelete = (contact) => {
    setContact({
      contactId: contact.contactId || "",
      firstName: contact.firstName || "",
      lastName: contact.lastName || "",
      phone: formattedPhone(contact.phone || ""),
      rawPhone: contact.phone || "",
    });
    setShowDeleteConfirmation(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  //Triggers add emergency contact  button
  const handleAddContactOnClick = () => {
    setIsAddingContact(true);
    setShowAddModal(true);
  };

  //Triggers cancel button
  const handleCancel = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    resetForm();
    history.push("/emergency-contacts");
  };

  const resetForm = () => {
    setContact({
      contactId: "",
      firstName: "",
      lastName: "",
      phone: "",
      rawPhone: "",
    });
    setErrors({});
  };

  const handleHeaderBackClick = () => {
    history.push("/");
  };

  return (
    <div className={styles.container}>
      <Header header={t("Settings")} onClick={handleHeaderBackClick} />
      <Button
        buttonText={t("Add Emergency Contact")}
        className={styles.addNew}
        onClick={handleAddContactOnClick}
        disabled={isLoading}
      />
      {isLoading && (
        <div>
          <Loader color={"#415364"} size={85} />
        </div>
      )}
      <div>
        <ContactList
          contacts={contacts}
          onDelete={handleDelete}
          onEdit={handleEdit}
        />

        <Modal
          isOpen={isAddingContact ? showAddModal : showEditModal}
          onClose={handleCancel}
        >
          <div className={styles.modalContent}>
            {isAddingContact ? (
              <p className={styles.header}>{t("Add Emergency Contact")}</p>
            ) : (
              <p className={styles.header}>{t("Edit Contact")}</p>
            )}

            <InputItem
              type="text"
              id="fName"
              name="firstName"
              label={""}
              value={contact.firstName}
              setValue={(value) => handleChange("firstName", value)}
              placeholder={t("First Name")}
              errors={errors.firstName}
              isPhoneNumber={false}
            />
            <InputItem
              type="text"
              id="lName"
              name="lastName"
              label={""}
              value={contact.lastName}
              setValue={(value) => handleChange("lastName", value)}
              placeholder={t("Last Name")}
              errors={errors.lastName}
              isPhoneNumber={false}
            />
            <InputItem
              type="text"
              id="phone"
              name="phoneNumber"
              label={""}
              // value={contact.phone}
              value={formattedPhone(contact.phone)}
              setValue={(value) => handleChange("phone", value)}
              placeholder={t("Phone")}
              errors={errors.phoneNumber}
              isPhoneNumber={true}
            />
            {/* </form> */}

            <div className={styles.buttonContainer}>
              <Button
                buttonText={t("Cancel")}
                onClick={handleCancel}
                className={styles.btnCancel}
              />
              {isAddingContact ? (
                <Button buttonText={t("Save")} onClick={handleAddContact} />
              ) : (
                <Button
                  buttonText={t("Save")}
                  onClick={() => handleUpdateContact(contact)}
                />
              )}
            </div>
          </div>
        </Modal>

        {showDeleteConfirmation && (
          <Modal isOpen={showDeleteConfirmation}>
            <div className={styles.deleteContent}>
              <p className={styles.deleteHeader}>
                {t("Are you sure you want to delete contact")}
              </p>
              <p className={styles.deleteBody}>
                {contact.firstName} {contact.lastName}:{" "}
                {formattedPhone(contact.phone)}
              </p>
              <div className={styles.buttonContainer}>
                <Button
                  buttonText={t("Yes")}
                  onClick={() => handleDeleteContact(contact)}
                />
                <Button buttonText={t("No")} onClick={cancelDelete} />
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default EmergencyContacts;
